import { Component } from 'react';
import { Button, Card, CardBody, CardGroup, Col, Container, Form, Row } from 'reactstrap';
import { connect } from 'react-redux';
import InputGroup from '../../../elements/Forms/InputGroup';
import * as actions from '../../../actions';
import { Field, reduxForm, InjectedFormProps } from 'redux-form';
import approve from 'approvejs';
import queryString from 'query-string';
import history from '../../../helpers/history';
import { sample } from 'lodash';
import { isLocalhost } from '../../../registerServiceWorker';
import { getTranslationKey } from '../../../modules/utils/helpers';
import type { Notify } from '../../../typings/redux';

interface FieldConfig {
  title: string;
  type: string;
  placeholder: string;
  rules: {
    email?: { message: string };
    required?: { message: string };
  };
}

interface FieldsConfig {
  [key: string]: FieldConfig;
}

const fields: FieldsConfig = {
  email: {
    title: getTranslationKey('email'),
    type: 'text',
    placeholder: getTranslationKey('emailPlaceholder'),
    rules: {
      email: {
        message: getTranslationKey('emailErrorMessage')
      }
    }
  },
  password: {
    type: 'password',
    placeholder: getTranslationKey('password'),
    title: getTranslationKey('password'),
    rules: {
      required: { message: getTranslationKey('requiredInput') }
    }
  }
};

interface LoginState {
  error?: string;
  destinationAfterSignIn?: string;
  confirmed?: string;
  backgroundImage?: string;
  cannyCompanyId?: string;
  cannyRedirectUrl?: string;
}

interface LoginFormValues {
  email: string;
  password: string;
}

interface AuthState {
  authenticated?: boolean;
  [key: string]: any;
}

interface LoginProps {
  auth: AuthState;
  notify: Notify;
  loginUser: (user: any, notify: Notify) => void;
}

type CombinedProps = LoginProps & InjectedFormProps<LoginFormValues, LoginProps>;

class Login extends Component<CombinedProps, LoginState> {
  constructor(props: CombinedProps) {
    super(props);
    const { error, destinationAfterSignIn, confirmed, companyID, redirect } = queryString.parse(
      history.location.search
    );

    this.state = {
      error: error as string,
      destinationAfterSignIn: destinationAfterSignIn as string,
      confirmed: confirmed as string,
      backgroundImage: this.getRandomBackgroundUrl(),
      cannyCompanyId: companyID as string,
      cannyRedirectUrl: redirect as string
    };
  }

  componentDidMount() {
    if (this.props.notify) {
      if (this.state.confirmed) {
        this.props.notify({
          title: getTranslationKey('userActivityLog.CONFIRMED'),
          message: '',
          status: 'success',
          position: 'top-center',
          dismissible: true,
          dismissAfter: 5000
        });
      }

      if (this.state.error === '1') {
        setTimeout(
          () =>
            this.props.notify({
              title: getTranslationKey('error'),
              message: getTranslationKey('loginError'),
              status: 'error',
              position: 'top-center',
              dismissible: true,
              dismissAfter: 5000
            }),
          1000
        );
      }
    }
  }

  getRandomBackgroundUrl(): string {
    const backgroundUrls = [
      `${process.env.REACT_APP_PUBLIC_BUCKET_URL}/builder/login/flying-camera.jpg`,
      `${process.env.REACT_APP_PUBLIC_BUCKET_URL}/builder/login/camera-smartphone-laptop.jpg`,
      `${process.env.REACT_APP_PUBLIC_BUCKET_URL}/builder/login/workspace.jpg`
    ];

    return sample(backgroundUrls) || backgroundUrls[0];
  }

  submit = (values: LoginFormValues) => {
    const user: any = { ...values };
    user.email = user.email.toLowerCase();
    if (window.USER_TYPE) {
      user.type = window.USER_TYPE;
    }
    this.props.loginUser(user, this.props.notify);
  };

  render() {
    return (
      <div
        className="app flex-row align-items-center"
        style={{
          backgroundImage:
            window.USER_TYPE !== 'VST'
              ? `url(${this.state.backgroundImage})`
              : `url(${window.CDN_URL}/public/gallerypix-loginscreen.jpg)`,
          backgroundPosition: 'center',
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat',
          backgroundColor: window.USER_TYPE !== 'VST' ? 'gray' : 'unset'
        }}
      >
        <Container>
          <Row className="justify-content-center">
            <Col md={7}>
              <CardGroup>
                <Card>
                  <CardBody>
                    <Form
                      action={isLocalhost ? undefined : `${window.API_URL}/api/signin`}
                      method={isLocalhost ? undefined : 'POST'}
                      onSubmit={isLocalhost ? this.props.handleSubmit(this.submit) : undefined}
                    >
                      {window.USER_TYPE === 'SB' && (
                        <div className="text-center m-b-25">
                          <img
                            height="75"
                            src={process.env.REACT_APP_SB_LOGO_URL}
                            alt="scrappbook logo"
                            className="img-fluid"
                          />
                        </div>
                      )}

                      {Object.keys(fields).map((fieldKey, index) => (
                        <Field
                          component={InputGroup}
                          key={index}
                          name={fieldKey}
                          field={fields[fieldKey]}
                        />
                      ))}

                      <input
                        type="hidden"
                        name="destinationAfterSignIn"
                        value={this.state.destinationAfterSignIn}
                      />
                      <input type="hidden" name="type" value={window.USER_TYPE} />
                      <input
                        type="hidden"
                        name="loginOrigin"
                        value={encodeURIComponent(
                          `${window.location.protocol}//${window.location.host}`
                        )}
                      />
                      <input
                        type="hidden"
                        name="cannyCompanyId"
                        value={this.state.cannyCompanyId}
                      />
                      <input
                        type="hidden"
                        name="cannyRedirectUrl"
                        value={this.state.cannyRedirectUrl}
                      />
                      <Row>
                        <Col xs="12">
                          <Button
                            data-cy="component-login"
                            color="primary"
                            className="w-100-p"
                            disabled={this.props.invalid || this.props.submitting}
                            type="submit"
                          >
                            {getTranslationKey('login')}
                          </Button>
                        </Col>
                      </Row>
                      {window.USER_TYPE === 'SB' && (
                        <Row>
                          <Col className="m-t-10 text-center">
                            <a href={`${window.API_URL}/api/auth/facebook`}>Facebook Login</a>
                          </Col>
                        </Row>
                      )}

                      <Row>
                        <Col className="m-t-10 text-center">
                          <a href="/#/account/forgot-password/">
                            {getTranslationKey('forgotPasswordTitle')}?
                          </a>
                        </Col>
                      </Row>
                      {window.USER_TYPE === 'SB' && window.LANGUAGE_SW !== 2 && (
                        <Row>
                          <Col className="m-t-10 text-center">
                            <a
                              href={
                                window.IS_TEST
                                  ? '/#/register'
                                  : window.LANGUAGE_SW === 0
                                  ? 'https://scrappbook.de/registrieren'
                                  : 'https://scrappbook.de/en/register'
                              }
                            >
                              {getTranslationKey('noAccount')}
                            </a>
                          </Col>
                        </Row>
                      )}

                      {window.USER_TYPE === 'VST' && (
                        <Row className="m-t-20">
                          <Col xs="12">
                            <hr />
                            <a
                              target="_blank"
                              href="https://www.vst-pro.de/impressum/"
                              rel="noopener noreferrer"
                            >
                              {getTranslationKey('imprint')}
                            </a>
                            {' - '}
                            <a
                              target="_blank"
                              href="https://www.vst-pro.de/datenschutz/"
                              rel="noopener noreferrer"
                            >
                              {getTranslationKey('privacy')}
                            </a>
                            {' - '}
                            <a
                              target="_blank"
                              href="https://www.vst-pro.de/kontakt/"
                              rel="noopener noreferrer"
                            >
                              {getTranslationKey('contact')}
                            </a>
                            {' - '}
                            <a
                              target="_blank"
                              href="https://www.vst-pro.de/produktwelten/produkte/produkt/vst-gallerypix/"
                              rel="noopener noreferrer"
                            >
                              {getTranslationKey('productInfos')}
                            </a>
                          </Col>
                        </Row>
                      )}
                      {window.USER_TYPE === 'SB' && (
                        <Row className="m-t-20">
                          <Col xs="12">
                            <hr />
                            <a
                              target="_blank"
                              href={process.env.REACT_APP_IMPRINT_LINK}
                              rel="noopener noreferrer"
                            >
                              {getTranslationKey('imprint')}
                            </a>
                            {' - '}
                            <a
                              target="_blank"
                              href={process.env.REACT_APP_PRIVACY_LINK}
                              rel="noopener noreferrer"
                            >
                              {getTranslationKey('privacy')}
                            </a>
                            {' - '}
                            <a
                              target="_blank"
                              href={process.env.REACT_APP_PRIVACY_LINK}
                              rel="noopener noreferrer"
                              onClick={(e) => {
                                try {
                                  window.Intercom('show');
                                  e.preventDefault();
                                } catch (error) {
                                  console.log(error);
                                }
                              }}
                            >
                              {getTranslationKey('contact')}
                            </a>
                          </Col>
                        </Row>
                      )}
                    </Form>
                  </CardBody>
                </Card>
              </CardGroup>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }
}

interface ValidationErrors {
  [key: string]: string[];
}

function validate(values: LoginFormValues): ValidationErrors {
  const errors: ValidationErrors = {};

  Object.keys(fields).forEach((fieldKey) => {
    const result = approve.value(values[fieldKey as keyof LoginFormValues], fields[fieldKey].rules);
    if (result.errors.length > 0) {
      errors[fieldKey] = result.errors;
    }
  });

  return errors;
}

interface RootState {
  auth: AuthState;
  notify: Notify;
}

const LoginWithForm = reduxForm<LoginFormValues, LoginProps>({
  form: 'loginForm',
  validate
})(Login);

const ConnectedLogin = connect(
  (state: RootState) => ({
    auth: state.auth,
    notify: state.notify
  }),
  actions
)(LoginWithForm);

export default ConnectedLogin;
